import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import { Search } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FetchInProgressProperties } from "../../apis/Property";
import { GenerateShortToken } from "../../apis/Auth";
import Paginate from "../../components/shared/paginate";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

const columns = [
  "ID",
  "Title",
  "Host Name",
  "Host Email",
  "City",
  "State",
  "Country",
  "Status",
  "Progress",
  "Updated At",
  "Edit",
  "View",
];

const cellClass =
  "min-w-[140px] flex-1 px-1 content-center border-gray-500 break-words py-2 text-center dark:border-white";

const getProgressPercentage = (property) => {
  const values = Object.values(property.progress || {});
  const total = values.length;
  if (total === 0) return "0%";
  const completed = values.reduce((acc, value) => acc + (value ? 1 : 0), 0);
  const pct = Math.round((completed / total) * 100);
  return `${Math.min(100, pct)}%`;
};

const listingTitle = (property) =>
  property.title || property.propertyType?.itemName?.en || "Untitled";

const hostName = (property) =>
  [property.host?.firstName, property.host?.lastName].filter(Boolean).join(" ") ||
  "—";

const formatStatus = (status) => {
  if (!status) return "—";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const declineReason = (property) => {
  if (property.status !== "declined" || !property.declineHistory?.length) {
    return "";
  }
  return property.declineHistory[property.declineHistory.length - 1]?.reason || "";
};

const InProgressProperties = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.MANAGE_LISTINGS);
  const [shortToken, setShortToken] = useState("");
  const [properties, setProperties] = useState([]);
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  const [keyword, setKeyword] = useState("");
  const limit = "10";

  const tokenTimestampRef = useRef(0);
  const generatingPromiseRef = useRef(null);
  const TOKEN_TTL_MS = 3 * 60 * 1000;

  useEffect(() => {
    const generateShortToken = async () => {
      try {
        const response = await GenerateShortToken();
        const token = response.data?.token || "";
        setShortToken(token);
        tokenTimestampRef.current = Date.now();
      } catch (err) {
        toast.error("Failed to generate short token");
      }
    };
    generateShortToken();
  }, []);

  const getValidShortToken = async () => {
    if (
      shortToken &&
      tokenTimestampRef.current &&
      Date.now() - tokenTimestampRef.current < TOKEN_TTL_MS
    ) {
      return shortToken;
    }

    if (generatingPromiseRef.current) {
      try {
        await generatingPromiseRef.current;
        return shortToken;
      } catch {
        return "";
      }
    }

    generatingPromiseRef.current = (async () => {
      try {
        const response = await GenerateShortToken();
        const token = response.data?.token || "";
        setShortToken(token);
        tokenTimestampRef.current = Date.now();
        return token;
      } catch (err) {
        toast.error("Failed to generate short token");
        return "";
      } finally {
        generatingPromiseRef.current = null;
      }
    })();

    return (await generatingPromiseRef.current) || "";
  };

  const openClient = async (path) => {
    const token = await getValidShortToken();
    if (!token) return;
    window.open(
      `${process.env.REACT_APP_CLIENTSIDE_URL}${path}${path.includes("?") ? "&" : "?"}token=${token}`,
      "_blank",
    );
  };

  useEffect(() => {
    let cancelled = false;

    const fetchInProgress = async () => {
      try {
        const response = await FetchInProgressProperties(
          currentPage,
          limit,
          keyword.trim(),
        );
        if (cancelled) return;
        if (response.success) {
          setProperties(response?.data?.listings || []);
          setTotalPages(response?.data?.totalPages || 1);
        } else {
          toast.error(
            "Failed to fetch in progress listings. Please try again later.",
          );
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(
            "Failed to fetch in progress listings. Please try again later.",
          );
        }
      }
    };

    fetchInProgress();
    return () => {
      cancelled = true;
    };
  }, [currentPage, keyword]);

  useEffect(() => {
    setCurrentPage("1");
  }, [keyword]);

  const headerColumns = useMemo(() => columns, []);

  const renderActions = (property) => (
    <>
      {canManage ? (
        <button
          type="button"
          className="text-secondaryColor"
          onClick={() =>
            openClient(`/listing/create?propertyId=${property._id}`)
          }
        >
          Edit
        </button>
      ) : (
        <span className="text-gray-400">—</span>
      )}
    </>
  );

  return (
    <div className="page-content">
      <Container
        className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]"
        fluid
      >
        <div className="flex flex-col gap-6 p-6">
          <div className="flex flex-wrap items-center gap-4">
            <h1 className="text-2xl dark:text-[#c7d8eb]">
              In progress listings
            </h1>
            <Link to="/property" className="font-semibold text-secondaryColor">
              Approved listings
            </Link>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Drafts from every host that are not published and have not been
            submitted for review. This uses the same rules as a host&apos;s
            in-progress rooms.
          </p>
          <div className="flex flex-col border-gray-400 dark:border-white lg:flex lg:border lg:border-b-0">
            <div className="flex w-full flex-col justify-between gap-4 rounded-[6px_6px_0px_0px] border-x border-t border-gray-500 p-4 dark:border-white lg:flex-row lg:border-none">
              <div className="relative w-full max-w-[400px] rounded-md border border-gray-500 dark:border-white">
                <Search
                  size={16}
                  className="absolute bottom-1/2 left-2 translate-y-1/2"
                />
                <input
                  className="w-full rounded-md py-2 pl-8 pr-6 text-base dark:bg-darkBg2"
                  type="search"
                  placeholder="Search by title, host, city, or status"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <div className="hidden lg:flex">
                {headerColumns.map((column) => (
                  <div
                    key={column}
                    className={`${cellClass} border-y font-semibold`}
                  >
                    {column}
                  </div>
                ))}
              </div>
              {properties.length === 0 && (
                <div className="border-b border-gray-500 px-4 py-8 text-center dark:border-white">
                  No in progress listings found.
                </div>
              )}
              {properties.map((property) => {
                const reason = declineReason(property);
                return (
                  <React.Fragment key={property._id}>
                    <div className="hidden lg:flex">
                      <div className={`${cellClass} border-b`}>
                        {property._id}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {listingTitle(property)}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {hostName(property)}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {property.host?.email || "—"}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {property.location?.city?.en || "—"}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {property.location?.state || "—"}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {property.location?.country?.en || "—"}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        <div>{formatStatus(property.status)}</div>
                        {reason && (
                          <div className="mt-1 text-xs text-red-600">
                            {reason}
                          </div>
                        )}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {getProgressPercentage(property)}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {property.updatedAt?.toString()?.slice(0, 10) || "—"}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {renderActions(property)}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        <button
                          type="button"
                          className="text-secondaryColor"
                          onClick={() =>
                            openClient(`/rooms/${property._id}/preview`)
                          }
                        >
                          View
                        </button>
                      </div>
                    </div>
                    <div className="mb-6 block border-x border-y border-gray-500 dark:border-white lg:hidden">
                      <MobileField label="ID">{property._id}</MobileField>
                      <MobileField label="Title">
                        {listingTitle(property)}
                      </MobileField>
                      <MobileField label="Host Name">
                        {hostName(property)}
                      </MobileField>
                      <MobileField label="Host Email">
                        {property.host?.email || "—"}
                      </MobileField>
                      <MobileField label="City">
                        {property.location?.city?.en || "—"}
                      </MobileField>
                      <MobileField label="State">
                        {property.location?.state || "—"}
                      </MobileField>
                      <MobileField label="Country">
                        {property.location?.country?.en || "—"}
                      </MobileField>
                      <MobileField label="Status">
                        <div>{formatStatus(property.status)}</div>
                        {reason && (
                          <div className="text-xs text-red-600">{reason}</div>
                        )}
                      </MobileField>
                      <MobileField label="Progress">
                        {getProgressPercentage(property)}
                      </MobileField>
                      <MobileField label="Updated At">
                        {property.updatedAt?.toString()?.slice(0, 10) || "—"}
                      </MobileField>
                      <MobileField label="Edit">
                        {renderActions(property)}
                      </MobileField>
                      <MobileField label="View">
                        <button
                          type="button"
                          className="text-secondaryColor"
                          onClick={() =>
                            openClient(`/rooms/${property._id}/preview`)
                          }
                        >
                          View
                        </button>
                      </MobileField>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <Paginate
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
          />
        </div>
      </Container>
    </div>
  );
};

const MobileField = ({ label, children }) => (
  <div className="flex justify-between gap-2 border-b border-gray-500 p-2 dark:border-white">
    <div className="font-bold">{label}</div>
    <div className="text-right">{children}</div>
  </div>
);

export default InProgressProperties;
